import { makeStyles } from "@material-ui/core"
import clsx from "clsx"
import { FC, useCallback } from "react"
import { useQueryClient } from "@tanstack/react-query"
import {
  ClientFragment,
  Enum_Mobile_Permission_Status_Enum,
} from "../../generated/graphql"
import useClientMobileDeviceDetails from "../../hooks/useClientMobileDeviceDetails"
import useClientFeatureFlags from "../../hooks/useClientFeatureFlags"
import useOrganizationFeatureFlags from "../../hooks/useOrganizationFeatureFlags"
import { colors } from "../../utils/theme"
import MobileFeatureToggleRow from "../molecules/MobileFeatureToggleRow"

/**
 * Client-facing mobile settings panel.
 *
 * Displays device/app details (model, OS, app version, check-in time) plus the
 * client’s current mobile permission states (notifications, motion, location,
 * keyboard) as reported by `useClientMobileDeviceDetails()`.
 *
 * If the organization has these features enabled, it also renders per-client
 * feature toggles (e.g. DBT Diary, Vira Path). Toggling a feature upserts the
 * corresponding flag via `useClientFeatureFlags()` and then invalidates the
 * `GetClientData` query to refresh the client record.
 */
const ClientMobileSettings: FC<{ client: ClientFragment }> = ({ client }) => {
  const classes = useStyles()
  const queryClient = useQueryClient()
  const { permissions: deviceDetails } = useClientMobileDeviceDetails(client.id)

  const initialViraPathEnabled =
    client.users_user_feature_flags?.vira_path_enabled ?? false
  const initialDbtDiaryEnabled =
    client.users_user_feature_flags?.dbt_diary_enabled ?? false

  const { dbtDiary, viraPath } = useClientFeatureFlags(client.id, {
    dbtDiaryEnabled: initialDbtDiaryEnabled,
    viraPathEnabled: initialViraPathEnabled,
  })
  const { showDBTDiaryFeatures, showViraPathFeatures } =
    useOrganizationFeatureFlags()

  const effectiveDbtDiaryFeatureEnabled =
    typeof dbtDiary.enabled === "boolean"
      ? dbtDiary.enabled
      : initialDbtDiaryEnabled

  const effectiveViraPathFeatureEnabled =
    typeof viraPath.enabled === "boolean"
      ? viraPath.enabled
      : initialViraPathEnabled

  const {
    resetFeedback: resetDbtDiaryFeedback,
    setEnabled: setDbtDiaryEnabled,
  } = dbtDiary

  const {
    resetFeedback: resetViraPathFeedback,
    setEnabled: setViraPathEnabled,
  } = viraPath

  const handleSetDbtDiaryEnabled = useCallback(
    async (nextValue: boolean) => {
      resetDbtDiaryFeedback()
      try {
        await setDbtDiaryEnabled(nextValue)
        await queryClient.invalidateQueries({ queryKey: ["GetClientData"] })
      } catch (error) {
        console.error("Unable to update DBT Diary feature", error)
      }
    },
    [queryClient, resetDbtDiaryFeedback, setDbtDiaryEnabled]
  )

  const handleSetViraPathEnabled = useCallback(
    async (nextValue: boolean) => {
      resetViraPathFeedback()
      try {
        await setViraPathEnabled(nextValue)
        await queryClient.invalidateQueries({ queryKey: ["GetClientData"] })
      } catch (error) {
        console.error("Unable to update Vira Path feature", error)
      }
    },
    [queryClient, resetViraPathFeedback, setViraPathEnabled]
  )

  // Helper function to get the display text for the permission status
  function getPermissionDisplayText(
    status: Enum_Mobile_Permission_Status_Enum
  ) {
    switch (status) {
      case Enum_Mobile_Permission_Status_Enum.Always:
        return "Enabled, Always"
      case Enum_Mobile_Permission_Status_Enum.WhileInUse:
        return "Enabled, While In Use"
      case Enum_Mobile_Permission_Status_Enum.Granted:
        return "Enabled"
      case Enum_Mobile_Permission_Status_Enum.Denied:
        return "Disabled"
      case Enum_Mobile_Permission_Status_Enum.Undetermined:
        return "Undetermined"
      default:
        return "Undetermined"
    }
  }

  return (
    <>
      <div className={clsx(classes.container, classes.detailText)}>
        <div className={clsx(classes.deviceTitle, classes.title)}>
          Device Details
        </div>
        <div className={clsx(classes.deviceModel, classes.subtitle)}>
          Device Model
        </div>
        <div className={classes.deviceModelStatus}>
          {deviceDetails.deviceModel}
        </div>
        <div className={clsx(classes.deviceOS, classes.subtitle)}>
          Operating System
        </div>
        <div className={classes.deviceOSStatus}>{deviceDetails.os}</div>
        <div className={clsx(classes.appVersion, classes.subtitle)}>
          App Version
        </div>
        <div className={classes.appVersionStatus}>
          {deviceDetails.appVersion}
        </div>
        <div className={classes.appVersionDetails}>
          {deviceDetails.updateAvailable && (
            <div className={classes.appVersionAlert}>
              A new version of the app is available.
            </div>
          )}
        </div>
        <div className={clsx(classes.settingsTitle, classes.title)}>
          App Settings
        </div>
        <div className={clsx(classes.notifications, classes.subtitle)}>
          Notifications
        </div>
        <div className={classes.notificationsStatus}>
          {getPermissionDisplayText(deviceDetails.notifications)}
        </div>
        <div className={classes.notificationsDetails}>
          Notifications are sent for check-ins, nudges, assessments, and
          practitioner connections.
        </div>
        <div className={clsx(classes.motion, classes.subtitle)}>Motion</div>
        <div className={classes.motionStatus}>
          {getPermissionDisplayText(deviceDetails.motion)}
        </div>
        <div className={classes.motionDetails}>
          Motion data are used to derive activity and sleep patterns.
        </div>
        <div className={clsx(classes.location, classes.subtitle)}>Location</div>
        <div className={classes.locationStatus}>
          {getPermissionDisplayText(deviceDetails.gps)}
        </div>
        <div className={classes.locationDetails}>
          Location data are used to derive time-at-home and activity patterns.
        </div>
        <div className={clsx(classes.keyboard, classes.subtitle)}>Keyboard</div>
        <div className={classes.keyboardStatus}>
          {getPermissionDisplayText(deviceDetails.keyboard)}
        </div>
        <div className={classes.keyboardDetails}>
          Keyboard data are used to calculate language features.
        </div>
        <div className={clsx(classes.checkinTime, classes.subtitle)}>
          Check-in Notification Time
        </div>
        <div className={classes.checkinTimeStatus}>
          {deviceDetails.checkinTime}
        </div>
        <div className={classes.checkinTimeDetails}>
          When this client receives a notification to check in.
        </div>
        {(showDBTDiaryFeatures || showViraPathFeatures) && (
          <div className={classes.enabledFeaturesSection}>
            <div className={classes.title}>Enabled Features</div>
            {showDBTDiaryFeatures && (
              <MobileFeatureToggleRow
                label="DBT Diary Feature"
                description="Controls access to the DBT Diary card workflow inside the mobile app."
                enabled={effectiveDbtDiaryFeatureEnabled}
                onSetEnabled={handleSetDbtDiaryEnabled}
                isLoading={dbtDiary.isUpserting}
                errorMessage={dbtDiary.error}
              />
            )}
            {showViraPathFeatures && (
              <MobileFeatureToggleRow
                label="Vira Path Features"
                description="Controls access to Vira Path features inside the mobile app."
                enabled={effectiveViraPathFeatureEnabled}
                onSetEnabled={handleSetViraPathEnabled}
                isLoading={viraPath.isUpserting}
                errorMessage={viraPath.error}
              />
            )}
          </div>
        )}
      </div>
    </>
  )
}

const useStyles = makeStyles((theme) => ({
  container: {
    padding: theme.spacing(3),
    border: `1px solid ${colors.neutral.clay}`,
    borderRadius: 4,
    display: "grid",
    gridTemplateAreas: `
      "deviceTitle deviceTitle deviceTitle"
      "deviceModel deviceModelStatus . "
      "deviceOS deviceOSStatus . "
      "appVersion appVersionStatus appVersionDetails "
      "settingsTitle settingsTitle settingsTitle"
      "notifications notificationsStatus notificationsDetails"
      "motion motionStatus motionDetails"
      "location locationStatus locationDetails"
      "keyboard keyboardStatus keyboardDetails"
      "checkinTime checkinTimeStatus checkinTimeDetails"
      "enabledFeatures enabledFeatures enabledFeatures"
    `,
    gridTemplateRows: "auto",
    gridTemplateColumns: "1fr 1fr 3fr",
    gridGap: theme.spacing(3),
  },
  title: {
    fontSize: 18,
    fontWeight: "bold",
  },
  deviceTitle: {
    gridArea: "deviceTitle",
  },
  deviceModel: {
    gridArea: "deviceModel",
  },
  deviceModelStatus: {
    gridArea: "deviceModelStatus",
  },
  deviceOS: {
    gridArea: "deviceOS",
  },
  deviceOSStatus: {
    gridArea: "deviceOSStatus",
  },
  settingsTitle: {
    gridArea: "settingsTitle",
    paddingTop: theme.spacing(3),
  },
  appVersion: {
    gridArea: "appVersion",
  },
  appVersionStatus: {
    gridArea: "appVersionStatus",
  },
  appVersionDetails: {
    gridArea: "appVersionDetails",
  },
  notifications: {
    gridArea: "notifications",
  },
  notificationsStatus: {
    gridArea: "notificationsStatus",
  },
  notificationsDetails: {
    gridArea: "notificationsDetails",
  },
  motion: {
    gridArea: "motion",
  },
  motionStatus: {
    gridArea: "motionStatus",
  },
  motionDetails: {
    gridArea: "motionDetails",
  },
  location: {
    gridArea: "location",
  },
  locationStatus: {
    gridArea: "locationStatus",
  },
  locationDetails: {
    gridArea: "locationDetails",
  },
  keyboard: {
    gridArea: "keyboard",
  },
  keyboardStatus: {
    gridArea: "keyboardStatus",
  },
  keyboardDetails: {
    gridArea: "keyboardDetails",
  },
  enabledFeaturesSection: {
    gridArea: "enabledFeatures",
    paddingTop: theme.spacing(3),
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(2),
  },
  checkinTime: {
    gridArea: "checkinTime",
  },
  checkinTimeStatus: {
    gridArea: "checkinTimeStatus",
  },
  checkinTimeDetails: {
    gridArea: "checkinTimeDetails",
  },
  detailText: {
    fontWeight: "normal",
    fontSize: 16,
  },
  subtitle: {
    fontWeight: 700,
  },
  appVersionAlert: {
    backgroundColor: colors.primary.cloud,
    border: `1px solid ${colors.primary.ice}`,
    borderRadius: theme.spacing(0.5),
    padding: theme.spacing(1.25, 1.5),
    width: "100%",
    fontWeight: 600,
    color: colors.primary.midnight,
    boxShadow: "none",
  },
}))

export default ClientMobileSettings
